import { getAvailableCarriers, getAvailableMethods } from '../../store/assets/carriers/utils';
import { orchestrationService } from '~/services';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { arrayToMap, useQueryCreator } from '../useQueryCreator';
import { useSession } from '../useSession';
import type { CarrierCarrierInfo, CarrierServiceMethodInfo } from '@shipium-corp/shipium-ts-types';
import type {
  SummaryCarrier,
  SummaryServiceMethod,
  CarrierMethodsView,
} from '~/services/orchestration-service/carriers-and-methods.types';

type CarriersFor<V extends CarrierMethodsView> = V extends 'summary' ? SummaryCarrier[] : CarrierCarrierInfo[];
type ServiceMethodsFor<V extends CarrierMethodsView> = V extends 'summary'
  ? SummaryServiceMethod[]
  : CarrierServiceMethodInfo[];

/**
 * @deprecated use useCarriers instead
 * Hook that replaces useStoreState((state) => state.carrierAssets.carriers)
 * Provides filtered carriers based on admin status, using React Query for data fetching
 *
 * Note: This hook returns empty object while loading for backward compatibility.
 * Use useCarriersState() to check loading/error states, or use useCarriers() directly.
 */
export function useCarriersFiltered<V extends CarrierMethodsView = 'full'>({ view }: { view?: V } = {}) {
  type Carrier = CarriersFor<V>[number];
  const carriersQuery = useCarriers({
    view,
    select: (carriers) => arrayToMap(carriers as Carrier[], 'carrierId' as keyof Carrier),
  });
  return (carriersQuery.data ?? {}) as Record<string, Carrier>;
}

/**
 * @deprecated use useServiceMethods instead
 * Hook that replaces useStoreState((state) => state.carrierAssets.carrierServiceMethods)
 * Provides filtered carrier service methods based on admin status, using React Query for data fetching
 *
 * Note: This hook returns empty object while loading for backward compatibility.
 * Use useCarriersState() to check loading/error states, or use useCarriers() directly.
 */
export function useCarrierServiceMethods<V extends CarrierMethodsView = 'full'>({
  view,
  withReturnServiceMethods = false,
}: { view?: V; withReturnServiceMethods?: boolean } = {}) {
  type Method = ServiceMethodsFor<V>[number];
  const carriersQuery = useServiceMethods({
    view,
    withReturnServiceMethods,
    select: (serviceMethods) => arrayToMap(serviceMethods as Method[], 'serviceMethodId' as keyof Method),
  });
  return (carriersQuery.data ?? {}) as Record<string, Method>;
}

export function useServiceMethods<V extends CarrierMethodsView = 'full', T = ServiceMethodsFor<V>>({
  view,
  withReturnServiceMethods = false,
  select = (serviceMethods) => serviceMethods as unknown as T,
}: {
  view?: V;
  withReturnServiceMethods?: boolean;
  select?: (serviceMethods: ServiceMethodsFor<V>) => T;
} = {}): UseQueryResult<T> {
  const { isShipiumView, sessionPartnerId } = useSession();
  const resolvedView = (view ?? 'full') as V;

  const carriersQuery = useQuery({
    ...useQueryCreator(orchestrationService.carriersAndMethods.search, {
      args: [resolvedView],
    }),
    enabled: !!sessionPartnerId,
    select: (data): T => {
      type Carrier = CarriersFor<V>[number];
      type Method = ServiceMethodsFor<V>[number];
      const carriersRecord = (data as unknown as Carrier[]).reduce<Record<string, Carrier>>((acc, carrier) => {
        if (carrier.carrierId) {
          acc[carrier.carrierId] = carrier;
        }
        return acc;
      }, {});

      const methods = Object.values(
        getAvailableMethods(carriersRecord, { isAdmin: !!isShipiumView, withReturnServiceMethods }),
      ) as Method[];
      return select(methods as ServiceMethodsFor<V>);
    },
  });

  return carriersQuery as UseQueryResult<T>;
}

/**
 * @deprecated use useCarriers instead
 * Hook that provides carriers as an array (like the old carrierList)
 * Useful for components that need the array format instead of a Record
 *
 * Note: This hook returns empty array while loading for backward compatibility.
 * Use useCarriersState() to check loading/error states, or use useCarriers() directly.
 */
export const useCarriersList = () => {
  const carriersQuery = useCarriers();
  return carriersQuery.data ?? [];
};

export function useCarriers<V extends CarrierMethodsView = 'full', T = CarriersFor<V>>({
  view,
  select = (carriers) => carriers as unknown as T,
}: {
  view?: V;
  select?: (carriers: CarriersFor<V>) => T;
} = {}): UseQueryResult<T> {
  const { isShipiumView, sessionPartnerId } = useSession();
  const resolvedView = (view ?? 'full') as V;

  const carriersQuery = useQuery({
    ...useQueryCreator(orchestrationService.carriersAndMethods.search, {
      args: [resolvedView],
    }),
    enabled: !!sessionPartnerId,
    select: (data): T => {
      type Carrier = CarriersFor<V>[number];
      const carriersRecord = (data as unknown as Carrier[]).reduce<Record<string, Carrier>>((acc, carrier) => {
        if (carrier.carrierId) {
          acc[carrier.carrierId] = carrier;
        }
        return acc;
      }, {});

      const carriers = Object.values(getAvailableCarriers(carriersRecord, !!isShipiumView));
      return select(carriers as CarriersFor<V>);
    },
  });

  return carriersQuery as UseQueryResult<T>;
}
